<template>
  <div
    v-if="modifiedSalesDocument"
    class="flex flex-col max-h-full gap-4 p-4 overflow-y-auto bg-white">
    <div class="flex flex-wrap w-full h-full gap-6">
      <BaseSelectDropdown
        name="company"
        :mode="mode"
        :items="dropdownCompanies"
        :selected-item="selectedCompany"
        :label="$t('document-type.company')"
        @update:selected="handleCompanySelection"
        clearable
        showErrorAsTooltip
        :error="$v.company.required.$invalid ? $v.company.required.$message : ''"
        class="min-w-48" />

      <BaseSelectDropdown
        name="companySite"
        :mode="mode"
        :items="dropdownSalesCompanySites"
        :selected-item="selectedCompanySite"
        :label="$t('salesDocuments.headers.companySite')"
        :error="$v.companySite.required.$invalid ? $v.companySite.required.$message : ''"
        clearable
        showErrorAsTooltip
        @update:selected="handleDocumentCompanySiteSelection"
        class="min-w-48" />

      <div class="max-w-48">
        <label for="description" class="text-sm font-medium">{{
          $t('salesDocuments.headers.description')
        }}</label>
        <BaseInput
          v-model="modifiedSalesDocument.documentHeader.headerRemarks"
          color="base"
          :disabled="mode === 'view'" />
      </div>

      <BaseSelectDropdown
        name="documentType"
        :mode="mode"
        :items="dropdownSalesDocumentTypes"
        :selected-item="selectedDocumentType"
        :label="$t('salesDocuments.headers.type')"
        @update:selected="handleDocumentTypeSelection"
        clearable
        showErrorAsTooltip
        :error="$v.documentType.required.$invalid ? $v.documentType.required.$message : ''"
        class="min-w-48" />

      <BaseSelectDropdown
        name="documentSeries"
        :mode="mode"
        :items="dropdownDocumentSeries"
        :selected-item="selectedDocumentSeries"
        :label="$t('salesDocuments.headers.series')"
        :disable="dropdownDocumentSeries.length == 0"
        clearable
        showErrorAsTooltip
        :error="
          dropdownDocumentSeries.length === 0
            ? `${$t('salesDocuments.missingDocumentSeries')}`
            : $v.documentSeries.required.$invalid
              ? $v.documentSeries.required.$message
              : ''
        "
        @update:selected="handleDocumentSeriesSelection"
        class="min-w-48" />
      <div class="max-w-48">
        <label for="docNumber" class="text-sm font-medium">{{
          $t('salesDocuments.headers.number')
        }}</label>
        <BaseInput
          type="number"
          :min="0"
          :disabled="
            mode === 'view' ||
            !modifiedSalesDocument.documentHeader.isFromManager ||
            seriesHasAutomaticNumbering
          "
          color="base"
          showErrorAsTooltip
          :error="$v.documentNumber.required.$invalid ? $v.documentNumber.required.$message : ''"
          v-model="modifiedSalesDocument.documentHeader.documentNumber" />
      </div>

      <div class="max-w-48">
        <label for="officialDate" class="text-sm font-medium">{{
          $t('salesDocuments.headers.date')
        }}</label>
        <DateTimePicker
          ref="officialDatePickerRef"
          v-model="selectedOfficialDate"
          :disabled="mode === 'view'"
          :show-time-picker="false"
          start-empty
          show-error-as-tooltip
          @update:modelValue="onOfficialDateChange()">
        </DateTimePicker>
      </div>

      <BaseSelectDropdown
        name="documentStatuses"
        :mode="mode"
        :items="dropdownDocumentStatuses"
        :selected-item="selectedDocumentStatus"
        :label="$t('salesDocuments.headers.status')"
        @update:selected="handleDocumentStatusSelection"
        clearable
        showErrorAsTooltip
        :error="$v.documentStatus.required.$invalid ? $v.documentStatus.required.$message : ''"
        class="min-w-48" />
    </div>

    <div class="flex flex-wrap w-full gap-6">
      <div class="max-w-56">
        <label for="customer" class="text-sm font-medium">{{
          $t('salesDocuments.headers.customer')
        }}</label>
        <div class="flex items-center justify-start gap-1">
          <BaseInput
            v-model="customerName"
            :disabled="mode === 'view'"
            color="base"
            readonly
            showErrorAsTooltip
            :error="$v.customerId.required.$invalid ? $v.customerId.required.$message : ''"
            @click="onCustomerClick">
            <template #suffix>
              <div class="h-full text-gray-400" @click.stop="clearCustomer">
                <IconXmark class="w-5 h-full mr-1 py-auto" />
              </div>
            </template>
          </BaseInput>
          <button class="p-1" @click="getBalanceByCustomerId">
            <IconSearch class="text-gray-400 cursor-pointer size-4 hover:text-theme-600" />
          </button>
        </div>
      </div>

      <div class="max-w-48">
        <label for="billingAddress" class="text-sm font-medium">{{
          $t('salesDocuments.headers.traderSite')
        }}</label>
        <BaseInput
          v-model="traderSiteName"
          :disabled="mode === 'view'"
          readonly
          color="base"
          @click="onTraderSiteClick"
          show-error-as-tooltip
          :error="$v.traderSiteId.required.$invalid ? $v.traderSiteId.required.$message : ''" />
      </div>

      <BaseSelectDropdown
        name="salesPerson"
        :mode="mode"
        :items="dropDownSalesPersons"
        :selected-item="selectedSalesPerson"
        :label="$t('salesDocuments.headers.salesPerson')"
        clearable
        showErrorAsTooltip
        :error="$v.salesPersonId.required.$invalid ? $v.salesPersonId.required.$message : ''"
        @update:selected="handleDocumentSalesPersonSelection"
        class="min-w-48" />

      <div class="max-w-48">
        <label for="mediator" class="text-sm font-medium">{{
          $t('salesDocuments.headers.mediator')
        }}</label>
        <BaseInput
          v-model="mediatorName"
          readonly
          color="base"
          @click="onMediatorClick"
          :disabled="mode === 'view'" />
      </div>

      <BaseSelectDropdown
        name="warehouse"
        :mode="mode"
        :items="dropdownWarehouses"
        :selected-item="selectedWareHouse"
        :label="$t('salesDocuments.headers.warehouse')"
        clearable
        @update:selected="handleDocumentWarehouseSelection"
        class="min-w-48"
        show-error-as-tooltip
        :error="
          $v.warehouseId.warehouseRule.$invalid ? $v.warehouseId.warehouseRule.$message : ''
        " />

      <!-- <div class="max-w-48">
        <label for="defaultQuantity" class="text-sm font-medium">{{
          $t('salesDocuments.headers.defaultQuantity')
        }}</label>
        <BaseInput color="base" type="number" :min="0" />
      </div> -->

      <div class="flex flex-wrap items-center w-full gap-6">
        <BaseSelectDropdown
          name="paymentType"
          :mode="mode"
          :items="dropdownPaymentTypes"
          :selected-item="selectedPaymentType"
          :label="$t('salesDocuments.headers.paymentType')"
          clearable
          @update:selected="handleDocumentPaymentTypeSelection"
          class="min-w-48"
          show-error-as-tooltip
          :error="
            $v.paymentTypeId.paymentTypeRule.$invalid
              ? $v.paymentTypeId.paymentTypeRule.$message
              : ''
          " />

        <BaseButton class="mt-5">e-payment</BaseButton>

        <BaseSelectDropdown
          name="deliveryType"
          :mode="mode"
          :items="dropdownDeliveryTypes"
          :selected-item="selectedDeliveryType"
          :label="$t('salesDocuments.headers.deliveryType')"
          clearable
          @update:selected="handleDocumentDeliveryTypeSelection"
          class="min-w-48"
          show-error-as-tooltip
          :error="
            $v.deliveryTypeId.deliveryTypeRule.$invalid
              ? $v.deliveryTypeId.deliveryTypeRule.$message
              : ''
          " />

        <div class="max-w-48">
          <label for="deliveryDate" class="text-sm font-medium">{{
            $t('salesDocuments.headers.deliveryDate')
          }}</label>
          <DateTimePicker
            ref="deliveryDatePickerRef"
            v-model="selectedDeliveryDate"
            :disabled="mode === 'view'"
            :show-time-picker="false"
            start-empty
            show-error-as-tooltip
            @update:modelValue="onDeliveryDateChange">
          </DateTimePicker>
        </div>

        <div class="max-w-48">
          <label for="billingAddress" class="text-sm font-medium">{{
            $t('salesDocuments.headers.billingAddress')
          }}</label>
          <BaseInput
            v-model="billingAddressName"
            :disabled="mode === 'view'"
            readonly
            color="base"
            @click="onBillingAddressClick"
            show-error-as-tooltip
            :error="
              $v.billingAddressId.required.$invalid ? $v.billingAddressId.required.$message : ''
            " />
        </div>

        <div class="max-w-48">
          <label for="deliveryAddress" class="text-sm font-medium">{{
            $t('salesDocuments.headers.deliveryAddress')
          }}</label>
          <BaseInput
            color="base"
            :disabled="mode === 'view'"
            v-model="modifiedSalesDocument.documentHeader.deliveryAddress" />
        </div>
      </div>
    </div>

    <SalesDocumentsModalTraderSites
      v-if="modalTraderSiteActive"
      :title="$t('salesDocuments.traderSiteAddModalSelect')"
      :list="traderSiteList"
      :total-records="traderSiteListCount"
      @refresh-data="onTraderSiteClick"
      @row-select="onTraderSiteSelect"
      @dismiss="onDismissTraderSiteModal" />

    <SalesDocumentsModalTraderSites
      v-if="modalBillingAddressActive"
      :title="$t('salesDocuments.billingAddModalSelect')"
      :list="billingAddressList"
      :total-records="billingAddressListCount"
      @refresh-data="onBillingAddressClick"
      @row-select="onBillingAddressSelect"
      @dismiss="onDismissBillingAddressModal" />

    <SalesDocumentsModalCustomer
      v-if="modalCustomerActive"
      :list="customersList"
      :total-records="customersListCount"
      @refresh-data="onCustomerClick"
      @row-select="onCustomerSelect"
      @dismiss="onDismissCustomerModal" />

    <SalesDocumentsModalMediator
      v-if="modalMediatorActive"
      :list="mediatorsList"
      :total-records="mediatorsListCount"
      @refresh-data="onMediatorClick"
      @row-select="onMediatorSelect"
      @dismiss="onDismissMediatorModal" />

    <InfoModal
      v-if="showBOCustomerBalanceModal"
      :title="$t('customer.financialInfo')"
      @primary-button-clicked="showBOCustomerBalanceModal = false">
      <template #content>
        <div v-if="htmlContent" v-html="htmlContent" class="overflow-y-auto max-h-72"></div>
        <div v-else-if="!selectedTraderSite || !selectedTraderSite.boid">
          <div style="font-weight: 600">{{ $t('messages.errors.somethingWentWrong') }}</div>
          <div>{{ $t('messages.errors.selectTraderSiteFirst') }}</div>
        </div>
        <div v-else>
          <div style="font-weight: 600">{{ $t('messages.errors.somethingWentWrong') }}</div>
          <div>{{ $t('messages.errors.missingFinancialInfo') }}</div>
        </div>
      </template>
    </InfoModal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useSalesDocumentsStore } from '@/stores/salesDocuments';
import { useToastStore } from '@/stores/toast';
import { useLookupsStore } from '@/stores/lookups';
import {
  getCustomerHtmlTabs,
  getCustomersWithConditions,
  getTraderSitesWithConditions,
  getTradersWithConditions,
} from '@/services/api/traders';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';

const { t } = useI18n();
import { helpers, minValue, required, requiredIf } from '@vuelidate/validators';
import { v4 as uuidv4 } from 'uuid';

import IconXmark from '@/assets/icons/IconXmark.vue';

import BaseInput from '@/components/common/BaseInput.vue';
import BaseSelectDropdown from '@/components/common/BaseSelectDropdown.vue';
import DateTimePicker from '@/components/common/DateTimePicker.vue';
import BaseButton from '@/components/common/BaseButton.vue';
import SalesDocumentsModalTraderSites from './SalesDocumentsModalTraderSites.vue';
import SalesDocumentsModalCustomer from './SalesDocumentsModalCustomer.vue';
import SalesDocumentsModalMediator from './SalesDocumentsModalMediator.vue';
import IconSearch from '@/assets/icons/IconSearch.vue';
import InfoModal from '@/components/common/InfoModal.vue';

import type { SelectDropdownItem, TableFilterData } from '@/types/Base';
import { type Trader, type Customer, type TraderSite } from '@/types/Traders';
import { isoDateToDatepickerFormat } from '@/utils/date';
import { eDivision } from '@/types/Division';
import type { MWParam } from '@/types/MW';

const {
  modifiedSalesDocument,
  clearForm,
  isModified,
  salesDocument,
  docDetailLines,
  rangeLineIds,
} = storeToRefs(useSalesDocumentsStore());

const { generateLinesByRange, saveDocumentLineInStore } = useSalesDocumentsStore();

const {
  dropdownDocumentTypes,
  dropdownDocumentStatuses,
  dropDownSalesPersons,
  dropdownWarehouses,
  dropdownPaymentTypes,
  dropdownDeliveryTypes,
  dropdownCompanies,
  dropdownCompanySites,
  deviceConfigurations,
  documentSeriesTypes,
  documentSeries,
  documentTypes,
  warehouses,
  divisions,
  companySites,
} = storeToRefs(useLookupsStore());

const {
  getDocumentSeriesLookup,
  getDeviceConfigurationsLookup,
  getDocumentSeriesTypesLookup,
  getDocumentTypesLookup,
  getCompanySitesLookup,
} = useLookupsStore();

const toastStore = useToastStore();

const selectedDocumentType = ref<SelectDropdownItem | null>(null);
const selectedDocumentSeries = ref<SelectDropdownItem | null>(null);
const selectedDocumentStatus = ref<SelectDropdownItem | null>(null);
const selectedSalesPerson = ref<SelectDropdownItem | null>(null);
const selectedWareHouse = ref<SelectDropdownItem | null>(null);
const selectedPaymentType = ref<SelectDropdownItem | null>(null);
const selectedDeliveryType = ref<SelectDropdownItem | null>(null);
const selectedCustomer = ref<Partial<Customer>>();
const selectedMediator = ref<Partial<Trader>>();
const selectedTraderSite = ref<Partial<TraderSite>>();
const selectedBillingAddress = ref<TraderSite>();
const selectedCompany = ref<SelectDropdownItem | null>(null);
const selectedCompanySite = ref<SelectDropdownItem | null>(null);
const selectedOfficialDate = ref<string>('');
const selectedDeliveryDate = ref<string>('');
const officialDatePickerRef = ref();
const deliveryDatePickerRef = ref();
const dropdownSalesDocumentTypes = ref<SelectDropdownItem[]>([]);
const props = defineProps<{
  mode: 'view' | 'edit';
}>();

onMounted(async () => {
  documentSeriesTypes.value = [];
  documentSeries.value = [];
  documentTypes.value = [];
  await getDeviceConfigurationsLookup();
  await getDocumentSeriesTypesLookup();
  await getDocumentSeriesLookup();
  await getDocumentTypesLookup();
  await getCompanySitesLookup();
  initProperties();
  await initDropDowns();
  $v.value.$reset();
  isModified.value = false;
});

watch(
  () => modifiedSalesDocument.value,
  (newData) => {
    validationModel.value.company = newData?.documentHeader.companyId || '';
    validationModel.value.companySite = newData?.documentHeader.companySiteId || '';
    validationModel.value.documentType = newData?.documentHeader.documentTypeId || '';
    validationModel.value.documentSeries = newData?.documentHeader.documentSeriesId || '';
    validationModel.value.documentNumber = newData?.documentHeader.documentNumber || '';
    validationModel.value.documentStatus = newData?.documentHeader.documentStatusId || '';
    validationModel.value.customerId = newData?.customerId || '';
    validationModel.value.traderSiteId = newData?.documentHeader.traderSiteId || '';
    validationModel.value.salesPersonId = newData?.salesPersonId || '';
    validationModel.value.billingAddressId = newData?.documentHeader.billingAddressId || '';
    validationModel.value.warehouseId = newData?.documentHeader.warehouseId || '';
    validationModel.value.deliveryTypeId = newData?.deliveryTypeId || '';
    validationModel.value.paymentTypeId =
      newData?.documentHeader.documentPayment?.paymentTypeId || '';
    validationModel.value.documentTypeObj = newData?.documentHeader.documentType || undefined;
    validationModel.value.documentPaymentObj = newData?.documentHeader.documentPayment || undefined;
  },
  { deep: true }
);

watch(
  () => clearForm.value,
  async () => {
    await initDropDowns();
    initProperties();
    clearForm.value = false;
  }
);

const dropdownDocumentSeries = computed(() => {
  let series = documentSeries.value.filter((x) => x.isActive);
  if (modifiedSalesDocument.value?.documentHeader.isFromManager) {
    const dcSeriesIds = new Set(deviceConfigurations.value.map((dc) => dc.documentSeriesID));
    series = series.filter((s) => !dcSeriesIds.has(s.id));
    if (selectedDocumentType.value && selectedDocumentType.value.value) {
      const validSeriesIds = new Set(
        documentSeriesTypes.value
          .filter((dst) => dst.documentTypeID === selectedDocumentType.value?.value)
          .map((dst) => dst.documentSeriesID)
      );
      series = series.filter((s) => validSeriesIds.has(s.id));
    }
  }
  return series.length
    ? (series.map((item) => ({
        value: item.id,
        name: item.name,
      })) as SelectDropdownItem[])
    : [];
});

const dropdownSalesCompanySites = computed(() => {
  if (!modifiedSalesDocument.value?.documentHeader.companyId) return [];
  const siteIDsOFSelectedCompany = companySites.value
    .filter((x) => x.companyID === modifiedSalesDocument.value?.documentHeader.companyId)
    .map((x) => x.id);
  return dropdownCompanySites.value.filter((x) =>
    siteIDsOFSelectedCompany.includes(x.value.toString())
  );
});

function initProperties() {
  if (!modifiedSalesDocument.value) return;
  if (
    salesDocument.value?.documentHeader.officialDate &&
    modifiedSalesDocument.value?.documentHeader.officialDate
  ) {
    selectedOfficialDate.value = isoDateToDatepickerFormat(
      modifiedSalesDocument.value?.documentHeader.officialDate
    );
  } else {
    modifiedSalesDocument.value.documentHeader.officialDate = undefined;
    selectedOfficialDate.value = '';
  }

  if (
    salesDocument.value?.documentHeader.deliveryDate &&
    modifiedSalesDocument.value?.documentHeader.deliveryDate
  )
    selectedDeliveryDate.value = isoDateToDatepickerFormat(
      modifiedSalesDocument.value.documentHeader.deliveryDate
    );
  else {
    let daysToDeliveryParam = undefined;
    const memoryParams = localStorage.getItem('mwParams');
    if (memoryParams) {
      const params = JSON.parse(memoryParams) as MWParam[];
      if (params && params.length > 0) {
        daysToDeliveryParam = params.find((x) => x.code === 'DAYS_TO_DELIVERY')?.defaultValue;
      }
    }
    const daysToDelivery =
      Number(daysToDeliveryParam) ||
      modifiedSalesDocument.value?.documentHeader.documentType?.daysToDelivery ||
      0;
    const date = new Date();
    date.setDate(date.getDate() + daysToDelivery);
    selectedDeliveryDate.value = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    ).toLocaleDateString('en-GB');
    modifiedSalesDocument.value.documentHeader.deliveryDate = selectedDeliveryDate.value;
  }

  if (modifiedSalesDocument.value?.customer)
    selectedCustomer.value = modifiedSalesDocument.value.customer;
  else selectedCustomer.value = undefined;

  if (modifiedSalesDocument.value?.documentHeader.mediator)
    selectedMediator.value = modifiedSalesDocument.value.documentHeader.mediator;
  else selectedMediator.value = undefined;

  if (modifiedSalesDocument.value?.documentHeader.billingAddress)
    selectedBillingAddress.value = modifiedSalesDocument.value.documentHeader.billingAddress;
  else selectedBillingAddress.value = undefined;

  if (modifiedSalesDocument.value?.documentHeader.traderSiteId)
    selectedTraderSite.value = modifiedSalesDocument.value.documentHeader.traderSite;
  else selectedTraderSite.value = undefined;

  if (
    modifiedSalesDocument.value.documentHeader.documentTypeId &&
    !modifiedSalesDocument.value.documentHeader.documentType
  )
    modifiedSalesDocument.value.documentHeader.documentType = documentTypes.value.find(
      (x) => x.id === modifiedSalesDocument.value?.documentHeader.documentTypeId
    );
}

const seriesHasAutomaticNumbering = ref(true);

async function initDropDowns() {
  if (!modifiedSalesDocument.value) return;

  const salesDivisionIds = divisions.value
    ?.filter((d) => d.origin === eDivision.Sales)
    .map((x) => x.id);
  const salesDocumentTypes = documentTypes.value
    .filter((x) => salesDivisionIds.includes(x.divisionID))
    .map((x) => x.id);
  dropdownSalesDocumentTypes.value = dropdownDocumentTypes.value.filter((x) =>
    salesDocumentTypes.includes(x.value.toString())
  );
  const documentType = dropdownDocumentTypes.value.find(
    (item) => item.value == modifiedSalesDocument.value?.documentHeader.documentTypeId
  );
  if (documentType) selectedDocumentType.value = documentType;
  else selectedDocumentType.value = null;

  const documentStatus = dropdownDocumentStatuses.value.find(
    (item) => item.value == modifiedSalesDocument.value?.documentHeader.documentStatusId
  );
  if (documentStatus) selectedDocumentStatus.value = documentStatus;
  else selectedDocumentStatus.value = null;

  const documentSeries = dropdownDocumentSeries.value.find(
    (item) => item.value == modifiedSalesDocument.value?.documentHeader.documentSeriesId
  );
  if (documentSeries) selectedDocumentSeries.value = documentSeries;
  else selectedDocumentSeries.value = null;

  const salesPerson = dropDownSalesPersons.value.find(
    (item) => item.value == modifiedSalesDocument.value?.salesPersonId
  );
  if (salesPerson) selectedSalesPerson.value = salesPerson;
  else selectedSalesPerson.value = null;

  const wareHouse = dropdownWarehouses.value.find(
    (item) => item.value == modifiedSalesDocument.value?.documentHeader.warehouseId
  );
  if (wareHouse) selectedWareHouse.value = wareHouse;
  else selectedWareHouse.value = null;

  const paymentType = dropdownPaymentTypes.value.find(
    (item) =>
      item.value == modifiedSalesDocument.value?.documentHeader.documentPayment?.paymentType?.id
  );
  if (paymentType) selectedPaymentType.value = paymentType;
  else selectedPaymentType.value = null;

  const deliveryType = dropdownDeliveryTypes.value.find(
    (item) => item.value == modifiedSalesDocument.value?.deliveryTypeId
  );
  if (deliveryType) selectedDeliveryType.value = deliveryType;
  else selectedDeliveryType.value = null;

  const company = dropdownCompanies.value.find(
    (item) => item.value == modifiedSalesDocument.value?.documentHeader?.companyId
  );
  if (company) selectedCompany.value = company;

  selectedCompanySite.value =
    dropdownCompanySites.value.find(
      (item) => item.value == modifiedSalesDocument.value?.documentHeader?.companySiteId
    ) ?? null;
  if (!selectedCompanySite.value && dropdownSalesCompanySites.value.length === 1) {
    selectedCompanySite.value = dropdownSalesCompanySites.value[0];
    modifiedSalesDocument.value.documentHeader.companySiteId =
      dropdownSalesCompanySites.value[0].value.toString();
  }
}

function handleDocumentTypeSelection(item: SelectDropdownItem) {
  selectedDocumentType.value = item ? item : null;
  if (modifiedSalesDocument.value)
    modifiedSalesDocument.value.documentHeader.documentTypeId = item
      ? item.value.toString()
      : undefined;
  const type = documentTypes.value.find((x) => x.id === item?.value);
  if (!modifiedSalesDocument.value || !type) return;
  modifiedSalesDocument.value.documentHeader.documentType = type;
  modifiedSalesDocument.value.documentHeader.divisionId = type.divisionID;

  const typeCompany = dropdownCompanies.value.find((c) => c.value === type.companyID);
  if (typeCompany) handleCompanySelection(typeCompany);
  if (
    !modifiedSalesDocument.value.documentHeader.warehouseId &&
    modifiedSalesDocument.value.documentHeader.documentSeriesId &&
    documentSeriesTypes.value
  ) {
    const defaultWarehouseID = documentSeriesTypes.value.find(
      (x) =>
        x.documentTypeID === item.value.toString() &&
        x.documentSeriesID === modifiedSalesDocument.value?.documentHeader.documentSeriesId
    )?.defaultWarehouseID;
    const typeWarehouse = dropdownWarehouses.value.find((w) => w.value === defaultWarehouseID);
    if (typeWarehouse) handleDocumentWarehouseSelection(typeWarehouse);
  }

  if (
    !modifiedSalesDocument.value.documentHeader.documentStatusId &&
    type.defaultDocumentStatusID
  ) {
    const typeStatus = dropdownDocumentStatuses.value.find(
      (s) => s.value === type.defaultDocumentStatusID
    );
    if (typeStatus) handleDocumentStatusSelection(typeStatus);
  }

  if (!modifiedSalesDocument.value.deliveryTypeId && type.defaultDeliveryTypeID) {
    const typeDelivery = dropdownDeliveryTypes.value.find(
      (t) => t.value === type.defaultDeliveryTypeID
    );
    if (typeDelivery) handleDocumentDeliveryTypeSelection(typeDelivery);
  }

  if (modifiedSalesDocument.value.customer?.defaultPaymentTypeId) {
    const paymentType = dropdownPaymentTypes.value.find(
      (t) => t.value === modifiedSalesDocument.value?.customer?.defaultPaymentTypeId
    );
    if (paymentType) handleDocumentPaymentTypeSelection(paymentType);
  } else if (!modifiedSalesDocument.value.documentHeader.documentPayment) {
    const typePayment = dropdownPaymentTypes.value.find(
      (t) => t.value === type.defaultPaymentTypeID
    );
    if (typePayment) handleDocumentPaymentTypeSelection(typePayment);
  }

  const date = new Date();
  date.setDate(date.getDate() + (type.daysToDelivery ?? 0));
  selectedDeliveryDate.value = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  ).toLocaleDateString('en-GB');
  modifiedSalesDocument.value.documentHeader.deliveryDate = selectedDeliveryDate.value;

  selectedDocumentSeries.value = null;
}

function handleDocumentSeriesSelection(item: SelectDropdownItem) {
  selectedDocumentSeries.value = item ? item : null;
  if (modifiedSalesDocument.value) {
    modifiedSalesDocument.value.documentHeader.documentSeriesId = item
      ? item.value.toString()
      : undefined;
    const series = documentSeries.value.find(
      (s) => s.id === modifiedSalesDocument.value?.documentHeader.documentSeriesId
    );
    if (series) seriesHasAutomaticNumbering.value = series.hasAutomaticNumbering;

    if (
      !modifiedSalesDocument.value.documentHeader.warehouseId &&
      modifiedSalesDocument.value.documentHeader.documentTypeId &&
      documentSeriesTypes.value
    ) {
      const defaultWarehouseID = documentSeriesTypes.value.find(
        (x) =>
          x.documentSeriesID === item.value.toString() &&
          x.documentTypeID === modifiedSalesDocument.value?.documentHeader.documentTypeId
      )?.defaultWarehouseID;

      const typeWarehouse = dropdownWarehouses.value.find((w) => w.value === defaultWarehouseID);
      if (typeWarehouse) handleDocumentWarehouseSelection(typeWarehouse);
    }
  }
}

function handleDocumentStatusSelection(item: SelectDropdownItem) {
  selectedDocumentStatus.value = item ? item : null;
  if (modifiedSalesDocument.value) {
    modifiedSalesDocument.value.documentHeader.documentStatusId = item
      ? item.value.toString()
      : undefined;
    if (
      modifiedSalesDocument.value.documentHeader.documentStatusId ===
      dropdownDocumentStatuses.value.find((s) => s.name == 'Closed')?.value
    )
      modifiedSalesDocument.value.documentHeader.officialDate = isoDateToDatepickerFormat();
  }
}

function handleDocumentSalesPersonSelection(item: SelectDropdownItem) {
  selectedSalesPerson.value = item ? item : null;
  if (modifiedSalesDocument.value) {
    modifiedSalesDocument.value.salesPersonId = item ? item.value.toString() : undefined;
  }
}

function handleDocumentWarehouseSelection(item: SelectDropdownItem) {
  selectedWareHouse.value = item ? item : null;
  if (modifiedSalesDocument.value) {
    modifiedSalesDocument.value.documentHeader.warehouseId = item
      ? item.value.toString()
      : undefined;
    const warehouse = warehouses.value.find((x) => x.id === item?.value);
    if (warehouse) modifiedSalesDocument.value.documentHeader.warehouse = warehouse;
  }
}

function handleDocumentPaymentTypeSelection(item: SelectDropdownItem) {
  selectedPaymentType.value = item ? item : null;
  if (modifiedSalesDocument.value && !modifiedSalesDocument.value.documentHeader.documentPayment) {
    modifiedSalesDocument.value.documentHeader.documentPayment = item
      ? {
          id: uuidv4(),
          paymentTypeId: item.value.toString(),
          startDate: '',
          endDate: '',
          name: item.name,
        }
      : undefined;
  } else {
    if (item) {
      modifiedSalesDocument.value!.documentHeader.documentPayment!.paymentTypeId =
        item.value.toString();
      modifiedSalesDocument.value!.documentHeader.documentPayment!.deleted = false;
    } else modifiedSalesDocument.value!.documentHeader.documentPayment!.deleted = true;
  }
}

function handleDocumentDeliveryTypeSelection(item: SelectDropdownItem) {
  selectedDeliveryType.value = item ? item : null;
  if (modifiedSalesDocument.value) {
    modifiedSalesDocument.value.deliveryTypeId = item ? item.value.toString() : undefined;
    if (item)
      modifiedSalesDocument.value.deliveryType = {
        id: item.value.toString(),
        name: item.name,
      };
    else modifiedSalesDocument.value.deliveryType = undefined;
  }
}

function handleCompanySelection(item: SelectDropdownItem) {
  const newCompany = selectedCompany.value?.value != item?.value;
  selectedCompany.value = item ? item : null;

  if (modifiedSalesDocument.value) {
    modifiedSalesDocument.value.documentHeader.companyId = item ? item.value.toString() : undefined;

    if (newCompany) {
      selectedCompanySite.value = null;
      modifiedSalesDocument.value.documentHeader.companySiteId = undefined;
      if (dropdownSalesCompanySites.value.length === 1) {
        handleDocumentCompanySiteSelection(dropdownSalesCompanySites.value[0]);
      }
    }
  }
}

function handleDocumentCompanySiteSelection(item: SelectDropdownItem) {
  selectedCompanySite.value = item ? item : null;
  if (modifiedSalesDocument.value) {
    modifiedSalesDocument.value.documentHeader.companySiteId = item
      ? item.value.toString()
      : undefined;
  }
}

function onOfficialDateChange() {
  if (modifiedSalesDocument.value && selectedOfficialDate.value != undefined) {
    if (
      selectedOfficialDate.value === '' &&
      modifiedSalesDocument.value.documentHeader.officialDate
    )
      // datepicker selection was canceled
      selectedOfficialDate.value = modifiedSalesDocument.value.documentHeader.officialDate;
    else modifiedSalesDocument.value.documentHeader.officialDate = selectedOfficialDate.value;
  }
}

function onDeliveryDateChange() {
  if (modifiedSalesDocument.value && selectedDeliveryDate.value != undefined) {
    if (
      selectedDeliveryDate.value === '' &&
      modifiedSalesDocument.value.documentHeader.deliveryDate
    )
      // datepicker selection was canceled
      selectedDeliveryDate.value = modifiedSalesDocument.value.documentHeader.deliveryDate;
    else modifiedSalesDocument.value.documentHeader.deliveryDate = selectedDeliveryDate.value;
  }
}

const modalTraderSiteActive = ref(false);
const modalBillingAddressActive = ref(false);
const modalCustomerActive = ref(false);
const modalMediatorActive = ref(false);

const customersList = ref<Customer[]>([]);
const customersListCount = ref();
const customerName = computed({
  get: () => {
    const trader = selectedCustomer.value?.trader;
    return trader?.name ? `${trader.code}-${trader.name}` : '';
  },
  set: (newValue: string) => {
    if (selectedCustomer.value?.trader?.name) {
      selectedCustomer.value.trader.name = newValue;
    }
  },
});

async function onCustomerClick(data?: TableFilterData, activateModal: boolean = true) {
  if (props.mode === 'view') return;
  if (docDetailLines.value.some((x) => x.isModified)) {
    toastStore.attention({
      text: $t('salesDocuments.saveLinesBeforeChangingSite'),
      hideTitle: true,
      timeout: 5000,
    });
    return;
  }
  customersList.value = [];
  const res = await getCustomersWithConditions({
    itemsPerPage: data?.itemsPerPage || 50,
    query: data?.query,
    traderId:
      !activateModal && modifiedSalesDocument.value?.documentHeader.traderSiteId
        ? modifiedSalesDocument.value.documentHeader.traderId
        : undefined,
    page: data?.page || 1,
    sortColumn: data?.sortColumn || 'id',
    sortAscending: data?.order === 'asc',
  });

  if (res) {
    customersList.value = res.data;
    customersListCount.value = res.count;
  }
  if (customersListCount.value === 1) onCustomerSelect(customersList.value[0], activateModal);
  else if (activateModal) modalCustomerActive.value = true;
}

function onDismissCustomerModal() {
  if (modifiedSalesDocument.value?.customer)
    selectedCustomer.value = modifiedSalesDocument.value.customer;
  modalCustomerActive.value = false;
}

async function onCustomerSelect(customer: Customer, isUserInput?: boolean) {
  selectedCustomer.value = customer;
  if (modifiedSalesDocument.value && modifiedSalesDocument.value.customerId !== customer.id) {
    modifiedSalesDocument.value.customerId = customer.id;
    if (
      modifiedSalesDocument.value.documentHeader.traderId != customer.traderId &&
      (isUserInput || isUserInput === undefined)
    ) {
      modifiedSalesDocument.value.documentHeader.billingAddress = undefined;
      modifiedSalesDocument.value.documentHeader.billingAddressId = undefined;
      modifiedSalesDocument.value.documentHeader.deliveryAddress = '';
      modifiedSalesDocument.value.documentHeader.traderSiteId = undefined;
      modifiedSalesDocument.value.documentHeader.traderSite = undefined;
      selectedBillingAddress.value = undefined;
      selectedTraderSite.value = undefined;
      modifiedSalesDocument.value.documentHeader.traderId = customer.traderId;
    }

    if (customer.defaultPaymentTypeId) {
      const paymentType = dropdownPaymentTypes.value.find(
        (t) => t.value === customer.defaultPaymentTypeId
      );
      if (paymentType) handleDocumentPaymentTypeSelection(paymentType);
    }
  }

  modalCustomerActive.value = false;

  if (isUserInput || isUserInput === undefined) {
    if (
      !selectedTraderSite.value ||
      selectedTraderSite.value.traderId !== selectedCustomer.value.traderId
    ) {
      const res = await getTraderSites({});
      traderSiteList.value = res.data;
      traderSiteListCount.value = res.count;
      if (traderSiteListCount.value === 1) await onTraderSiteSelect(traderSiteList.value[0], false);
    }
    await loadLinesFromRange();
  }
}

function clearCustomer() {
  selectedCustomer.value = undefined;
  if (modifiedSalesDocument.value) {
    modifiedSalesDocument.value.customerId = undefined;
    modifiedSalesDocument.value.customer = undefined;
    modifiedSalesDocument.value.documentHeader.traderId = undefined;
    modifiedSalesDocument.value.documentHeader.traderSiteId = undefined;
    modifiedSalesDocument.value.documentHeader.traderSite = undefined;
    modifiedSalesDocument.value.documentHeader.billingAddressId = undefined;
    modifiedSalesDocument.value.documentHeader.billingAddress = undefined;
    modifiedSalesDocument.value.documentHeader.deliveryAddress = '';
    if (modifiedSalesDocument.value.documentHeader.documentPayment)
      modifiedSalesDocument.value.documentHeader.documentPayment.deleted = true;

    selectedBillingAddress.value = undefined;
    selectedPaymentType.value = null;
    selectedTraderSite.value = undefined;
  }
}

const mediatorsList = ref<Partial<Trader>[]>([]);
const mediatorsListCount = ref();
const mediatorName = computed({
  get: () => selectedMediator.value?.name || '',
  set: (newValue: string) => {
    if (selectedMediator.value?.name) {
      selectedMediator.value.name = newValue;
    }
  },
});

async function onMediatorClick(data: TableFilterData) {
  if (props.mode === 'view') return;
  mediatorsList.value = [];
  modalMediatorActive.value = true;
  const res = await getTradersWithConditions({
    itemsPerPage: data?.itemsPerPage || 50,
    query: data?.query,
    page: data?.page || 1,
    sortColumn: data?.sortColumn || 'id',
    sortAscending: data?.order === 'asc',
  });
  if (res) {
    mediatorsList.value = res.data;
    mediatorsListCount.value = res.count;
  }
}

function onDismissMediatorModal() {
  if (modifiedSalesDocument.value?.documentHeader.mediator) {
    selectedMediator.value = modifiedSalesDocument.value.documentHeader.mediator;
  }
  modalMediatorActive.value = false;
}

function onMediatorSelect(mediator: Partial<Trader>) {
  selectedMediator.value = mediator;
  if (modifiedSalesDocument.value)
    modifiedSalesDocument.value.documentHeader.mediatorId = mediator.id;
  modalMediatorActive.value = false;
}

const traderSiteList = ref<TraderSite[]>([]);
const traderSiteListCount = ref();
const traderSiteName = computed({
  get: () => selectedTraderSite.value?.address || '',
  set: (newValue: string) => {
    if (selectedTraderSite.value?.address) selectedTraderSite.value.address = newValue;
  },
});
async function onTraderSiteClick(data: TableFilterData) {
  if (props.mode === 'view') return;
  if (docDetailLines.value.some((x) => x.isModified)) {
    toastStore.attention({
      text: $t('salesDocuments.saveLinesBeforeChangingSite'),
      hideTitle: true,
      timeout: 5000,
    });
    return;
  }
  traderSiteList.value = [];
  modalTraderSiteActive.value = true;
  const res = await getTraderSites(data);
  traderSiteList.value = res.data;
  traderSiteListCount.value = res.count;
  if (traderSiteListCount.value === 1) await onTraderSiteSelect(traderSiteList.value[0]);
}

function onDismissTraderSiteModal() {
  if (modifiedSalesDocument.value?.documentHeader.traderSite)
    selectedTraderSite.value = modifiedSalesDocument.value.documentHeader.traderSite;
  modalTraderSiteActive.value = false;
}

async function onTraderSiteSelect(traderSite: TraderSite, isUserInput = true) {
  selectedTraderSite.value = traderSite;
  if (modifiedSalesDocument.value) {
    modifiedSalesDocument.value.documentHeader.traderSiteId = traderSite.id;
    modifiedSalesDocument.value.documentHeader.traderSite = {
      id: traderSite.id,
      code: traderSite.code || '',
      name: traderSite.name || '',
      address: traderSite.address || '',
    };
    modifiedSalesDocument.value.documentHeader.traderId = traderSite.traderId;
    await onBillingAddressSelect(traderSite, false);
  }
  modalTraderSiteActive.value = false;

  if (isUserInput || isUserInput === undefined) {
    await onCustomerClick(undefined, false);
    await loadLinesFromRange();
  }
}

const billingAddressList = ref<TraderSite[]>([]);
const billingAddressListCount = ref();
const billingAddressName = computed({
  get: () => selectedBillingAddress.value?.address || '',
  set: (newValue: string) => {
    if (selectedBillingAddress.value?.address) {
      selectedBillingAddress.value.address = newValue;
    }
  },
});
async function onBillingAddressClick(data: TableFilterData) {
  if (props.mode === 'view') return;
  if (docDetailLines.value.some((x) => x.isModified)) {
    toastStore.attention({
      text: $t('salesDocuments.saveLinesBeforeChangingSite'),
      hideTitle: true,
      timeout: 5000,
    });
    return;
  }
  billingAddressList.value = [];
  modalBillingAddressActive.value = true;
  const res = await getTraderSites(data);
  billingAddressList.value = res.data;
  billingAddressListCount.value = res.count;
}

async function getTraderSites(
  data: TableFilterData
): Promise<{ data: TraderSite[]; count: number }> {
  const res = await getTraderSitesWithConditions({
    itemsPerPage: data?.itemsPerPage || 50,
    query: data?.query || modifiedSalesDocument.value?.documentHeader?.traderId,
    page: data?.page || 1,
    sortColumn: data?.sortColumn || 'name',
    sortAscending: data?.order === 'asc',
    withCustomers: true,
    isActive: true,
  });
  return res;
}

function onDismissBillingAddressModal() {
  if (modifiedSalesDocument.value?.documentHeader.billingAddress)
    selectedBillingAddress.value = modifiedSalesDocument.value.documentHeader.billingAddress;
  modalBillingAddressActive.value = false;
}

async function onBillingAddressSelect(traderSite: TraderSite, isUserInput = true) {
  selectedBillingAddress.value = traderSite;
  if (modifiedSalesDocument.value) {
    modifiedSalesDocument.value.documentHeader.billingAddressId = traderSite.id;
    modifiedSalesDocument.value.documentHeader.billingAddress = traderSite;
    modifiedSalesDocument.value.documentHeader.deliveryAddress = traderSiteName.value;
    if (isUserInput || isUserInput === undefined) {
      await onTraderSiteSelect(traderSite, false);
      await onCustomerClick(undefined, false);
      await loadLinesFromRange();
    }
  }
  modalBillingAddressActive.value = false;
}

async function loadLinesFromRange() {
  if (modifiedSalesDocument.value && modifiedSalesDocument.value.documentHeader.documentDetails) {
    const rangeLineIdsSet = new Set(rangeLineIds.value);
    modifiedSalesDocument.value.documentHeader.documentDetails =
      modifiedSalesDocument.value.documentHeader.documentDetails.filter(
        (detail) => !rangeLineIdsSet.has(detail.id)
      );
    rangeLineIds.value = [];
  }

  const doc = modifiedSalesDocument.value;
  if (!doc) {
    toastStore.warning({ text: $t('salesDocuments.rangeFailDocNotFound'), hideTitle: true });
    return;
  }

  const { documentTypeId, traderSiteId } = doc.documentHeader;
  if (!documentTypeId || !doc.customerId || !traderSiteId) return;

  const lines = await generateLinesByRange(documentTypeId, doc.customerId, traderSiteId);
  rangeLineIds.value = lines.filter((x) => x).map((x) => x.id);
  if (lines?.length) await Promise.all(lines.map((line) => line && saveDocumentLineInStore(line)));

  toastStore.success({
    timeout: 5000,
    text: $t('salesDocuments.rangeSuccess', {
      linesNumber: lines?.length,
      docType: selectedDocumentType.value?.name,
      customer: customerName.value,
      traderSite: traderSiteName.value,
    }),
  });
}

// customer Balance

const showBOCustomerBalanceModal = ref(false);
const htmlContent = ref('');
async function getBalanceByCustomerId() {
  if (!selectedCustomer.value?.boid) return;
  htmlContent.value = '';
  let resp;
  if (selectedTraderSite.value && selectedTraderSite.value.boid) {
    resp = await getCustomerHtmlTabs({
      enumValue: 34,
      traderSiteBoid: selectedTraderSite.value?.boid,
    });
  } else {
    resp = await getCustomerHtmlTabs({
      enumValue: 1,
      customerBoid: selectedCustomer.value?.boid,
    });
  }

  if (resp) htmlContent.value = resp;

  showBOCustomerBalanceModal.value = true;
}

const { t: $t } = useI18n({ useScope: 'global' });
const validationModel = ref({
  company: modifiedSalesDocument.value?.documentHeader.companyId || '',
  companySite: modifiedSalesDocument.value?.documentHeader.companySiteId || '',
  documentType: modifiedSalesDocument.value?.documentHeader.documentTypeId || '',
  documentSeries: modifiedSalesDocument.value?.documentHeader.documentSeriesId || '',
  documentNumber: modifiedSalesDocument.value?.documentHeader.documentNumber || '',
  documentStatus: modifiedSalesDocument.value?.documentHeader.documentStatusId || '',
  customerId: modifiedSalesDocument.value?.customerId || '',
  salesPersonId: modifiedSalesDocument.value?.salesPersonId || '',
  traderSiteId: modifiedSalesDocument.value?.documentHeader.traderSiteId || '',
  billingAddressId: modifiedSalesDocument.value?.documentHeader.billingAddressId || '',
  warehouseId: modifiedSalesDocument.value?.documentHeader.warehouseId || '',
  paymentTypeId: modifiedSalesDocument.value?.documentHeader.documentPayment?.paymentTypeId || '',
  deliveryTypeId: modifiedSalesDocument.value?.deliveryTypeId || '',
  officialDate: modifiedSalesDocument.value?.documentHeader.officialDate || '',
  deliveryDate: modifiedSalesDocument.value?.documentHeader.deliveryDate || '',
  documentTypeObj: modifiedSalesDocument.value?.documentHeader.documentType || undefined,
  documentPaymentObj: modifiedSalesDocument.value?.documentHeader.documentPayment || undefined,
});

const rules = computed(() => ({
  company: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  companySite: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  documentType: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  documentSeries: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  documentNumber: {
    required: helpers.withMessage(
      `${$t('messages.errors.required')}`,
      requiredIf(!seriesHasAutomaticNumbering.value)
    ),
    minValue: helpers.withMessage(`${$t('messages.errors.greaterThan0')}`, minValue(1)),
  },
  documentStatus: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  customerId: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  salesPersonId: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  traderSiteId: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
  billingAddressId: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
  warehouseId: { warehouseRule },
  paymentTypeId: { paymentTypeRule },
  deliveryTypeId: { deliveryTypeRule },
  officialDate: {
    validDate: helpers.withMessage(t('messages.errors.invalidDateFormat'), () => {
      const validation = officialDatePickerRef.value?.getValidationState();
      return !validation || validation.isValid;
    }),
  },
  deliveryDate: {
    validDate: helpers.withMessage(t('messages.errors.invalidDateFormat'), () => {
      const validation = deliveryDatePickerRef.value?.getValidationState();
      return !validation || validation.isValid;
    }),
  },
}));

const warehouseRule = helpers.withMessage($t('messages.errors.required'), (value, siblingSate) => {
  return siblingSate.documentTypeObj.requireWarehouse ? !!value : true;
});

const paymentTypeRule = helpers.withMessage(
  $t('messages.errors.required'),
  (value, siblingSate) => {
    // Value is documentHeader.documentPaymentTypeId not the dropdown - Value
    if (!siblingSate.documentTypeObj?.requirePaymentType) return true; // If payment type not required there is never error
    if (value && !siblingSate.documentPaymentObj.deleted) return true; // If there is a value check if documentPayment is deleted
    return !!siblingSate.documentPaymentObj && siblingSate.documentPaymentObj.deleted === false; // If there is no value check if documentPayment exists and it's not deleted
  }
);

const deliveryTypeRule = helpers.withMessage(
  $t('messages.errors.required'),
  (value, siblingSate) => {
    if (!siblingSate.documentTypeObj?.requireDeliveryType) return true;
    return siblingSate.documentTypeObj.requireDeliveryType ? !!value : true;
  }
);

const $v = useVuelidate(rules, validationModel, { $lazy: true, $autoDirty: true });
</script>
